import { Container, Typography, Grid, CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import ProgressBar from 'Components/ProgressBar'
import { ServiceAndChildSelection } from 'Pages/CaregiverPortal/ServiceFinder/ServiceAndChildSelection'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { ServiceTimeSelection } from 'Pages/CaregiverPortal/ServiceFinder/ServiceTimeSelection'
import { type ReferralTimeFrame, type DesiredSchedule, SearchType, type service } from 'core/types'
import { ServiceProviderOptions } from 'Pages/CaregiverPortal/ServiceFinder/ServiceProviderOptions'
import { type ChildMemberDetails } from 'Pages/CaregiverPortal/Members/ChildDisplay'
import { ServiceProviderMapSelect, type ServiceProviderOption } from 'Pages/CaregiverPortal/Components/ServiceProviderMapSelect'
import { ServiceProviderListSelect } from 'Pages/CaregiverPortal/Components/ServiceProviderListSelect'
import { RequestSummary } from 'Pages/CaregiverPortal/ServiceFinder/RequestSummary'
import { toast } from 'react-toastify'

export function ServiceFinderStep (): JSX.Element {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [isdID, setIsdID] = useState<number>(0)
  const [child, setChild] = useState<ChildMemberDetails | null>(null)
  const [searchType, setSearchType] = useState<SearchType | null>(null)
  const [service, setService] = useState<service | null>(null)
  const enrollmentCounts = child?.enrollmentCountByService?.filter(s => s.serviceID === service?.id) ?? []
  const enrollmentCount = enrollmentCounts?.length > 0 ? enrollmentCounts[0].count : 0
  const [selectedFacilities, setSelectedFacilities] = useState<ServiceProviderOption[]>([])
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<ReferralTimeFrame | null>(null)
  const [selectedSchedule, setSelectedSchedule] = useState<DesiredSchedule | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [maxProviders, setMaxProviders] = useState<number | undefined>(undefined)

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response, success } = await sendGet('/CaregiverPortal/CaregiverPortalISDID', {})
      if (success) {
        const { response: maxProvidersResponse, success: maxProvidersSuccess } = await sendGet(`/CaregiverPortal/GetISDMaxProviders/${response as number}`, {})
        if (maxProvidersSuccess) {
          setMaxProviders(maxProvidersResponse ?? undefined)
        }

        setIsdID(response)
        setLoading(false)
      }
    }

    void getData()
  }, [])

  const serviceAndChildSelected = async (service: service, childID: number): Promise<void> => {
    setCurrentStep(currentStep + 1)

    setService(service)
    const { response, success } = await sendGet(`/CaregiverPortal/GetChildDetails/${childID}`, {})
    if (success) {
      setChild(response)
    }
  }

  const serviceTimeSelected = (timeOption: ReferralTimeFrame, schedule: DesiredSchedule, selectedDate: Date | null): void => {
    setSelectedTimeFrame(timeOption)
    setSelectedSchedule(schedule)
    setSelectedDate(selectedDate)
    setCurrentStep(currentStep + 1)
  }

  function facilitySelected (type: SearchType): void {
    setSearchType(type)
    if (type === SearchType.ReachOut) {
      setSelectedFacilities([])
      setCurrentStep(currentStep + 2)
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  function providersSelected (selectedProviders: ServiceProviderOption[]): void {
    setSelectedFacilities(selectedProviders)
    setCurrentStep(currentStep + 1)
  }

  function goToSelectMapOption (): void {
    setSearchType(SearchType.Map)
  }

  function goToSelectListOption (): void {
    setSearchType(SearchType.Filter)
  }

  const handleBack = (): void => {
    setCurrentStep(currentStep - 1)
  }

  const handleFinalStepBack = (): void => {
    if (searchType === SearchType.ReachOut) {
      setSelectedFacilities([])
      setCurrentStep(currentStep - 2)
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const submitRequest = async (orderedFacilityIDs: number[]): Promise<void> => {
    if (child == null) {
      toast.error('The request could not be submitted because a child was not selected')
    } else if (service == null) {
      toast.error('The request could not be submitted because a service was not selected')
    } else {
      const data = { isdID, childID: child.id, serviceID: service.id, facilityIDs: orderedFacilityIDs, timeFrame: selectedTimeFrame, desiredSchedule: selectedSchedule, scheduleDate: selectedDate }
      const { success } = await sendPost('/CaregiverPortal/SubmitServiceRequest', data)
      if (success) {
        toast.success('Your request was submitted successfully.')
      } else {
        toast.error('There was an error with submitting your request.')
      }
    }
  }

  if (loading) {
    return <CircularProgress />
  } else if (isdID === 0) {
    return <Typography variant='h6' component='div' data-testid='no-services' >
      The ISD offering you services could not be found. Please contact your ISD to request services.
    </Typography >
  } else {
    return <Container>
      <Typography variant='h4' sx={{ mb: '30px' }}>Service Finder</Typography>
      <Grid container sx={{ overflowX: 'hidden' }}>
        <Grid item xs={12} sx={{ mb: '20px' }}>
          <ProgressBar
            totalSteps={5}
            currentStep={currentStep}
          />
        </Grid>
        {/* Be aware that the components for the first 3 steps use copy/paste inheritance from InterestForm components.  The 2 for step 4 were made into shared components. */}
        {currentStep === 1 && <ServiceAndChildSelection isdID={isdID} dataSelected={serviceAndChildSelected} />}
        {currentStep === 2 && <ServiceTimeSelection isdID={isdID} handleBack={handleBack} dataSelected={serviceTimeSelected} />}
        {currentStep === 3 && <ServiceProviderOptions isdID={isdID} handleBack={handleBack} dataSelected={facilitySelected} />}
        {currentStep === 4 && child?.location != null && service != null && searchType === SearchType.Map &&
          <ServiceProviderMapSelect childDOB={child.dateOfBirth} childLocation={child.location} timeFrame={selectedTimeFrame} isdId={isdID} serviceId={service.id} isdServiceProviderId={null} selectedProviders={[]}
            handleBack={handleBack} handleContinue={providersSelected} handleSpecificProviderClick={goToSelectListOption} maxProviders={maxProviders} currentEnrollmentCount={enrollmentCount}
          />
        }
        {currentStep === 4 && child?.location != null && service != null && searchType === SearchType.Filter &&
          <ServiceProviderListSelect childDOB={child.dateOfBirth} timeFrame={selectedTimeFrame} childLocation={child.location} isdId={isdID} serviceId={service.id} isdServiceProviderId={null}
            handleBack={handleBack} handleContinue={providersSelected} handleMapSelectionClick={goToSelectMapOption} maxProviders={maxProviders} currentEnrollmentCount={enrollmentCount}
          />
        }
        {currentStep === 5 && child != null && service != null && <RequestSummary isdId={isdID} child={child} providers={selectedFacilities} service={service} handleBack={handleFinalStepBack} submitRequest={submitRequest} />}
      </Grid>
    </Container>
  }
}
